import React from 'react'
import ReactDataSheet from 'react-datasheet'
import 'react-datasheet/lib/react-datasheet.css'
import './DataSheet.css'
import Button from 'material-ui/Button'
import CircularProgress from 'material-ui/Progress/CircularProgress'
import { withStyles } from 'material-ui/styles'
import green from 'material-ui/colors/green'
import { getActualProductId, makeProductId } from './../products/id-handler'
import { getCurrentPrice, findProduct } from './../products/utils'
import { getOpeningQty } from './stock-level-tools'

const styles = theme => ({
  wrapper: {
    margin: theme.spacing.unit,
    position: 'relative'
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '20px',
    left: '60px',
    marginTop: -12,
    marginLeft: -12
  }
})

class DataSheet extends React.Component {
  /**
   * Used in checking if to use manifest price or the current price for the product
   * i.e if its the first time you are entering the product use the current price else
   * use the price from the manifest , if for some reasons the price is `undefined` as
   * string, make it 0 and use it, since it was not saved the last time
   * @param product
   * @param manifestItem the particular manifest item if passed use it else find it
   * @returns number
   */
  mayBeUseManifestPrice (product, manifestItem) {
    if (!manifestItem) {
      manifestItem = this.props.manifest.find(item => item.name === product.name) || {}
    }
    // for some old price that the price is undefined in string
    manifestItem.price = manifestItem.price === 'undefined' ? 0 : manifestItem.price
    if (manifestItem.price >= 0) {
      return manifestItem.price
    }

    return getCurrentPrice(product.prices).price
  }

  /**
   * new products picks last week's opening qty (on hand + given) else use the one
   * from manifest for products attached within the manifest
   * @param product
   * @param manifestItem
   *
   * @returns {number}
   */
  mayBeUseManifestOpeningQty (product, manifestItem) {
    if (!manifestItem) {
      manifestItem = this.props.manifest.find(item => item.name === product.name) || {}
    }
    // for some old manifest opening qty is undefined in string
    manifestItem.openingQty = manifestItem.openingQty === 'undefined' ? 0 : manifestItem.openingQty
    if (manifestItem.openingQty >= 0) {
      return manifestItem.openingQty
    }

    return getOpeningQty(product, this.props.prevWeekBalance)
  }

  /**
   * check if the quantity entered is >= 0 then add to manifest else remove it from the
   * manifest to be sent to the stop
   * @param rowI row index is used to track the row within the datasheet
   * @param the value of the current column currently editted
   */
  handleQtyChanged (rowI, value) {
    const {onUpdateManifestItem} = this.props
    const product = this.dataSheet.grid[rowI][1].product
    // if value is >= 0, add the product to manifest
    if (parseInt(value, 10) >= 0) {
      onUpdateManifestItem(product, { qty: value })
    } else {
      onUpdateManifestItem(product, { qty: '' })
    }
  }

  handleOpeningQtyChanged (rowI, value) {
    const {onUpdateManifestItem} = this.props
    const product = this.dataSheet.grid[rowI][1].product
    // if value is >= 0, add the product to manifest
    if (parseInt(value, 10) >= 0) {
      onUpdateManifestItem(product, { openingQty: value })
    } else {
      onUpdateManifestItem(product, { openingQty: getOpeningQty(product, this.props.prevWeekBalance) }) // go back to default value from last week
    }
  }

    /**
     * When product id column is altered or entered, we check to see if that product
     * id is existing then populate the row else leave it blank and not editable
     * @param rowI
     * @param value
     */
  handleProductChanged (rowI, value) {
    const {removeManifestItem, upsertManifestItem, products} = this.props
    const index = rowI - 1

    if (value === '') {
        // means remove the row from manifest
      removeManifestItem(index)
      return
    }

    // construct a new product when the product is not existing with the value as name
    const product = products.find(({_id}) => _id === makeProductId(value)) || {name: value}

    // means edit/insert new row in manifest
    upsertManifestItem(product, index, {
      openingQty: this.mayBeUseManifestOpeningQty(product),
      price: this.mayBeUseManifestPrice(product)
    })
  }

  isCellModified (cellValue, newValue) {
    // convert cellValue to string since newValue is always string
    const strCellValue = `${cellValue}`
    return strCellValue !== newValue
  }

  render () {
    const {
      stopName,
      products,
      manifest,
      onSaveChanges,
      isManifestSaving,
      classes,
      successMsg
    } = this.props

    let gridContent = []

    const getProductByName = name => findProduct(products, name, {lookBehind: true})

    // in field supply this should be the list of products a location subscribed to
    for (let i = 0; i < products.length; i++) {
      let manifestItem = manifest[i] || {}

      if (!manifestItem.name) {
        gridContent.push([
          { value: '' },
          { readOnly: true },
          { readOnly: true },
          { readOnly: true },
          { readOnly: true }
        ])
        continue
      }

      let product = getProductByName(manifestItem.name) || {}
      let productId = getActualProductId(product._id)
      let customClass = ''
      let quantitiesReadOnly = false
      if (!productId) {
        // no product found for this manifestItem.name
        // disable the quantities pasting
        customClass = 'bg-red'
        productId = manifestItem.name
        quantitiesReadOnly = true
        product = {name: productId}
      }

      gridContent.push([
        { value: productId, className: `left ${customClass}` },
        { readOnly: true, value: product.name, product, className: 'left' },
        { value: this.mayBeUseManifestOpeningQty(product, manifestItem), readOnly: quantitiesReadOnly },
        { value: manifestItem.qty || '', readOnly: quantitiesReadOnly },
        { readOnly: true, value: this.mayBeUseManifestPrice(product, manifestItem) }
      ])
    }

    this.dataSheet = {
      grid: [
        [
          { readOnly: true, value: 'Product ID' },
          { value: 'Product', readOnly: true },
          { value: 'Opening Quantity', readOnly: true },
          { value: 'Quantity', readOnly: true },
          { value: 'Price', readOnly: true }
        ],
        ...gridContent
      ]
    }

    const EDITABLE_COLUMNS = {
      PRODUCT: 0,
      QTY: 3,
      OPENINGQTY: 2
    }

    return (
      <div>
        <h3>{stopName}</h3>
        <div className={classes.wrapper}>
          <Button
            variant='raised'
            color='primary'
            disabled={isManifestSaving}
            onClick={() => onSaveChanges()}
          >
            Save Changes
          </Button>
          {isManifestSaving && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </div> {successMsg}
        <div className={'sheet-container'}>
          <ReactDataSheet
            data={this.dataSheet.grid}
            valueRenderer={cell => cell.value}
            onContextMenu={(e, cell, i, j) =>
              cell.readOnly ? e.preventDefault() : null
            }
            onChange={(modifiedCell, rowI, colJ, value) => {
              if (!this.isCellModified(modifiedCell.value, value)) {
                return
              }

              if (colJ === EDITABLE_COLUMNS.PRODUCT) {
                this.handleProductChanged(rowI, value)
              } else if (colJ === EDITABLE_COLUMNS.OPENINGQTY) {
                this.handleOpeningQtyChanged(rowI, value)
              } else if (colJ === EDITABLE_COLUMNS.QTY) {
                this.handleQtyChanged(rowI, value)
              }
            }}
          />
        </div>
      </div>
    )
  }
}

export default withStyles(styles)(DataSheet)



// WEBPACK FOOTER //
// ./src/jobs/DataSheet.js